import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { cache } from "@components/index/providers";
import MARK_NOTES_EXPORTED from "@graphql/mutations/MarkNotesExported";
import SEND_EXPORT_NOTE_EMAIL from "@graphql/mutations/SendExportNoteEmail";
import {
  useDefaultValues,
  useUploadProgress,
  useAutosave,
  useAutosaveContext,
  FindAndReplaceProvider,
  useLocalStorage,
  usePimsExportFormat,
} from "@hooks";
import { evictNoteCache } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import {
  MODIFY_NOTE,
  CREATE_PATIENT_NOTE,
  CREATE_QUICK_NOTE,
} from "./api/mutations";
import { submitBasicNote } from "./api/submit";
import { NoteForm } from "./form/NoteForm";
import { RecordingTranscriptDrawer } from "./form/RecordingTranscriptDrawer";
import { ProcessingNoteFormFallback } from "./ProcessingNoteFormFallback";
import { submitAttachments } from "../../attachments/attachment-utils";
import { alert } from "../../common/Alert";
import { FloatingWordMultiEditInput } from "../../common/inputs/FloatingWordMultiEditInput";
import { ErrorMessage } from "../../common/inputs/Input";
import { copyAll } from "../../notebook/note/utils";
import { formToNoteInput } from "../common/utils";

const UPLOAD_NOTE_ATTACHMENTS = gql`
  mutation uploadNoteAttachments(
    $noteUuid: String!
    $attachments: [AttachmentInput]!
    $createdAt: DateTime!
  ) {
    uploadNoteAttachments(
      noteUuid: $noteUuid
      attachments: $attachments
      createdAt: $createdAt
    ) {
      ok
    }
  }
`;

function EditableNoteFormWrapper(props) {
  const {
    note,
    isSuperSOAP,
    isQuicknote,
    isScribeEdit = false,
    shouldBillUserForNote,
    patientUUID,
    noteUUID,
    teamUUID,
    template,
    refetchNote,
    shouldShowTranscriptDrawer,
    setShouldShowTranscriptDrawer,
  } = props;

  const navigate = useNavigate();
  const [modifyNote] = useMutation(MODIFY_NOTE);
  const [createPatientNote] = useMutation(CREATE_PATIENT_NOTE);
  const [createQuickNote] = useMutation(CREATE_QUICK_NOTE);
  const [modifyNotes] = useMutation(MARK_NOTES_EXPORTED);
  const [uploadNoteAttachments] = useMutation(
    UPLOAD_NOTE_ATTACHMENTS,
  );
  const [sendExportNoteEmail] = useMutation(SEND_EXPORT_NOTE_EMAIL);
  const { cancelDebounce } = useAutosaveContext();
  const pimsFormat = usePimsExportFormat();

  const [shouldCopyOnSubmit] = useLocalStorage(
    "should-copy-on-submit",
    true,
  );

  const methods = useForm();
  const {
    control,
    getValues,
    setValue,
    reset,
    trigger,
    formState: { errors },
    resetField,
    handleSubmit,
  } = methods;

  const { setPercentageUploaded, setCurrentFileName } =
    useUploadProgress();

  const { cancelDebounceOnSubmit } = useFlags();

  // Create handlers using React Hook Form's handleSubmit
  const onSaveAsDraft = handleSubmit(async (data) => {
    if (cancelDebounceOnSubmit) {
      cancelDebounce();
    }

    // Set a flag to indicate this is a draft submission

    const isDraft = !isEditingCompletedNote;
    await onSubmit({ data, isDraft });
  });

  const onSubmitNote = handleSubmit(async (data) => {
    if (cancelDebounceOnSubmit) {
      cancelDebounce();
    }

    // Copy note if setting is enabled
    if (shouldCopyOnSubmit) {
      copyAll({
        note: data,
        isForSharedExport: true,
        format: pimsFormat,
      });
    }

    const isDraft = false;
    await onSubmit({ data, isDraft });
  });

  useDefaultValues({
    template,
    note,
    reset,
  });

  useAutosave({
    save: onSubmit,
    getValues,
    control,
  });

  async function onSubmit({
    data,
    isDraft = false,
    isAutosave = false,
  }) {
    const props = {
      isSuperSOAP,
      patientUUID,
      noteUUID,
      teamUUID,
    };
    const mutations = {
      modifyNote,
      createPatientNote,
      createQuickNote,
    };

    if (!isAutosave) {
      mixpanel.track("Note Submitted", {
        noteUUID: noteUUID,
      });
    }

    async function inlineEmailExport({ uuid }) {
      if (!isDraft && data?.toEmail) {
        try {
          await sendExportNoteEmail({
            variables: {
              toEmail: data?.toEmail,
              grantedNoteUuid: uuid,
              isForAssistant: false,
              specialInstructions: data?.specialInstructions,
              areAttachmentsIncluded: data?.includeAttachments,
              shouldEmbedEmailExports:
                data?.exportType === "embeddedText",
            },
          });
          alert("success", `Email sent to ${data?.toEmail}`);
          modifyNotes({
            variables: {
              noteUuids: [uuid],
              action: "mark_exported",
            },
          });
        } catch {
          alert(
            "error",
            "Email export unsuccessful. Please try again.",
          );
        }
      }
    }

    const result = await trigger();

    if (!result) {
      if (isAutosave) {
        return "form-errors";
      }
    } else {
      const structuredNoteData = formToNoteInput({
        data,
        shouldSaveAsGenericObjective: data?.shouldUseGenericObjective,
      });
      if (
        data.keyHistory ||
        data.keyMedications ||
        data.weight ||
        data.vaccinationStatus
      ) {
        mixpanel.track("Used Additional Key Information");
      }

      const { submittedNoteUuid } = await submitBasicNote({
        structuredNoteData,
        props,
        mutations,
        template,
        isDraft,
        isAutosave,
        isScribeEdit,
        shouldBillUserForNote,
        inlineEmailExport,
      });

      // Legacy flow: upload attachments on submit for notes without a UUID
      // (new notes created before save). Notes with a UUID handle uploads
      // directly via the AttachmentSelection "Save & Upload" button.
      if (!isAutosave && !noteUUID) {
        const uploadedFiles = await submitAttachments({
          getValues,
          setPercentageUploaded,
          setCurrentFileName,
        });

        if (uploadedFiles) {
          await uploadNoteAttachments({
            variables: {
              createdAt: moment().format(),
              noteUuid: submittedNoteUuid,
              attachments: uploadedFiles,
            },
          });
        }
      }

      if (!isSuperSOAP && !isScribeEdit && !isAutosave && !isDraft) {
        evictNoteCache(cache);
        navigate(-1); // Go back to the previous page. If we came from the patient notebook, go back to the patient notebook. If we came from the dashboard, go back to the dashboard. If we came from the new note menu, go back to the new note menu. You get the gist.
      }
      if (isScribeEdit && !isAutosave) {
        navigate("/scribe/selectnote", {
          state: { shouldRefetch: true },
        });
      }
    }
  }

  const isEditingCompletedNote = note?.isTagged && note?.isApproved;

  if (note?.isMarkedAsProcessing) {
    return <ProcessingNoteFormFallback note={note} />;
  }

  return (
    <div>
      <div className="fixed items-center z-30 bottom-0 left-0 right-0 mx-auto w-[stretch] w-[-webkit-fill-available] w-[-fill-available]">
        {note?.isAutoSoap &&
        !isSuperSOAP &&
        !isScribeEdit &&
        note?.jobType !== "dictation" ? (
          <RecordingTranscriptDrawer
            note={note}
            refetchNote={refetchNote}
            shouldShowTranscript={shouldShowTranscriptDrawer}
            onTranscriptVisibilityChange={
              setShouldShowTranscriptDrawer
            }
          />
        ) : null}
      </div>

      <FormProvider {...methods}>
        <FindAndReplaceProvider>
          <NoteForm
            note={note}
            refetchNote={refetchNote}
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            isSuperSOAP={isSuperSOAP}
            isQuicknote={isQuicknote}
            isScribeEdit={isScribeEdit}
            patientUUID={patientUUID}
            noteUUID={noteUUID}
            resetField={resetField}
            onSaveAsDraft={onSaveAsDraft}
            onSubmitNote={onSubmitNote}
          />
          <FloatingWordMultiEditInput />
        </FindAndReplaceProvider>

        <div className="flex flex-row items-center justify-center">
          {(errors?.dd || errors?.mm || errors?.yyyy) && (
            <ErrorMessage message="Oops! Looks like the note 'Created At' date you entered is invalid." />
          )}
          {errors?.customObjectiveFields && (
            <ErrorMessage message="Oops! Looks like one or more of your custom objective fields doesn't have a name." />
          )}
        </div>
      </FormProvider>
    </div>
  );
}

export default EditableNoteFormWrapper;
